import { Float32BufferAttribute, Mesh, Uint8BufferAttribute, type BufferGeometry } from 'three';
import type { PolygonWithHoles } from '../../geo/types';
import type { SiteModel } from '../../site/types';
import type { Layout, PaletteModel } from '../../data/types';
import type { MaterialFactory } from '../../render/types';
import type { VertexStyle } from '../mesh/meshBuilder';
import { createMeshBuilder } from '../mesh/meshBuilder';
import { ATTRIBUTE, SURFACE, EDGE_ID, WATER_KIND_CODE } from '../mesh/attributes';
import { WORLD } from '../../core/config';
import { centroid } from '../../geo/polygon';
import type { DensifyConfig } from './densify';
import {
  createWaterClassSampler,
  vertexWaterLabels,
  FALLBACK_WATER_CLASS,
  KIND_BOUNDARY_EDGE_M,
  type WaterBodyClasses,
  type WaterClassSampler,
} from './waterKinds';
import { classifyWaterAreas } from './waterAreas';
import { kattengatCoveredWater, kattengatDuikerAt } from './kattengatDuiker';

const GOLDEN_RATIO_FRACTION = 0.618033988749;

function waterStyle(palette: PaletteModel, index: number, strokeAngle: number): VertexStyle {
  return {
    color: palette[FALLBACK_WATER_CLASS].median.linear,
    surface: SURFACE.ground,
    edgeId: EDGE_ID.water,
    seed: (index * GOLDEN_RATIO_FRACTION) % 1,
    strokeAngle,
    extent: [0, 0],
  };
}

function flowAngle(site: SiteModel, polygon: PolygonWithHoles): number {
  const flow = site.queries.flowDirectionAt(centroid(polygon.outer));
  return Math.atan2(flow[1], flow[0]);
}

// Merged water polygons hold several kinds, and far-ring water continues the kind it extends from the
// bounds, so colour and kind come from the site's kind query at every vertex.
function labelByKind(
  geometry: BufferGeometry,
  sampler: WaterClassSampler,
  palette: PaletteModel
): BufferGeometry {
  const positions = geometry.getAttribute('position').array;
  const indices = geometry.getIndex()?.array ?? [];
  const labels = vertexWaterLabels(positions, indices, sampler);
  const colours = Float32Array.from(labels.flatMap(({ cls }) => palette[cls].median.linear));
  const kinds = Uint8Array.from(labels, ({ kind }) => WATER_KIND_CODE[kind]);
  geometry.setAttribute(ATTRIBUTE.color, new Float32BufferAttribute(colours, 3));
  geometry.setAttribute(ATTRIBUTE.waterKind, new Uint8BufferAttribute(kinds, 1));
  return geometry;
}

export function buildWaterMesh(
  site: SiteModel,
  layout: Layout,
  palette: PaletteModel,
  materials: MaterialFactory,
  densifyConfig?: DensifyConfig,
  bodies: WaterBodyClasses = {},
  retainedSampler?: WaterClassSampler
): Mesh {
  const sampler =
    retainedSampler ??
    createWaterClassSampler(
      site.queries,
      classifyWaterAreas(layout.water.areas, layout.land.cityEnclosure),
      bodies
    );
  // Class boundaries are refined along with the painter bend, the only refinement ground polygons take;
  // without it colours still follow the vertices, only the ramps between classes stay wide.
  const byClass: DensifyConfig | undefined = densifyConfig && {
    ...densifyConfig,
    boundaries: { labeller: sampler, minEdge: KIND_BOUNDARY_EDGE_M },
  };
  const builder = createMeshBuilder();
  site.water.forEach((polygon, index) => {
    const style = waterStyle(palette, index, flowAngle(site, polygon));
    builder.groundPolygon(style, polygon, WORLD.waterY, byClass);
  });
  site.farRing.water.forEach((polygon, index) => {
    const style = waterStyle(palette, site.water.length + index, 0);
    builder.groundPolygon(style, polygon, WORLD.waterY, byClass);
  });

  const geometry = labelByKind(builder.build(), sampler, palette);
  const mesh = new Mesh(geometry, materials.water());
  mesh.matrixAutoUpdate = false;
  mesh.frustumCulled = true;
  // A separate water patch below the dry bank: it is a documented covered drain, so its
  // kind is canal even where the surface-site query correctly identifies dry ground.
  const duiker = kattengatDuikerAt(site.waterEdges);
  if (duiker) {
    const covered = createMeshBuilder();
    const polygon = kattengatCoveredWater(duiker);
    const style = {
      ...waterStyle(palette, site.water.length, flowAngle(site, polygon)),
      color: palette.canalWater.median.linear,
    };
    covered.groundPolygon(style, polygon, WORLD.waterY, densifyConfig);
    const geometry = covered.build();
    geometry.setAttribute(
      ATTRIBUTE.waterKind,
      new Uint8BufferAttribute(
        new Uint8Array(geometry.getAttribute('position').count).fill(WATER_KIND_CODE.canal),
        1
      )
    );
    const belowBank = new Mesh(geometry, mesh.material);
    belowBank.name = 'kattengat-covered-water';
    belowBank.matrixAutoUpdate = false;
    mesh.add(belowBank);
  }
  return mesh;
}
